import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import DestinationCard from './DestinationCard'
import DestinationCardTwo from './DestinationCardTwo'
import SidebarWidgets from '../Common/SidebarWidgets'
import { api } from '../../services/api'
import { useContentList } from '../../hooks/useContent'

function DestinationInner() {
    const [activeTab, setActiveTab] = useState('tab-grid')
    const [currentPage, setCurrentPage] = useState(1)
    const [searchInput, setSearchInput] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [orderBy, setOrderBy] = useState('menu_order')
    const postsPerPage = 9

    const { items: posts, loading, error } = useContentList(
        () => api.getDestinations({
            per_page: 100,
            ...(searchQuery ? { search: searchQuery } : {}),
        }),
        [searchQuery]
    )

    const sortedPosts = useMemo(() => {
        const list = [...posts]
        const priceOf = (item) => {
            const raw = item.price_from ?? item.price ?? 0
            const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''))
            return Number.isFinite(n) ? n : 0
        }
        switch (orderBy) {
            case 'popularity':
                return list.sort((a, b) => (Number(b.tour_count) || 0) - (Number(a.tour_count) || 0))
            case 'rating':
                return list.sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0))
            case 'date':
                return list.sort((a, b) => Number(b.id) - Number(a.id))
            case 'price':
                return list.sort((a, b) => priceOf(a) - priceOf(b))
            case 'price-desc':
                return list.sort((a, b) => priceOf(b) - priceOf(a))
            default:
                return list
        }
    }, [posts, orderBy])

    const totalPages = Math.ceil(sortedPosts.length / postsPerPage) || 1
    const indexOfLastPost = currentPage * postsPerPage
    const indexOfFirstPost = indexOfLastPost - postsPerPage
    const currentPosts = sortedPosts.slice(indexOfFirstPost, indexOfLastPost)

    const handleSearch = (e) => {
        e.preventDefault()
        setSearchQuery(searchInput.trim())
        setCurrentPage(1)
    }

    const handleClearSearch = () => {
        setSearchInput('')
        setSearchQuery('')
        setCurrentPage(1)
    }

    const handlePageChange = (page) => {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <section className="space">
            <div className="container">
                {loading && <p className="text-center">Loading destinations...</p>}
                {error && <p className="text-center text-danger">{error}</p>}
                <div className="th-sort-bar">
                    <div className="row justify-content-between align-items-center">
                        <div className="col-md-4">
                            <div className="search-form-area">
                                <form className="search-form" onSubmit={handleSearch}>
                                    <input
                                        type="text"
                                        placeholder="Search destinations..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        aria-label="Search destinations"
                                    />
                                    <button type="submit" aria-label="Search">
                                        <i className="fa-light fa-magnifying-glass" />
                                    </button>
                                </form>
                            </div>
                            {searchQuery && (
                                <p className="mb-0 mt-2 small">
                                    Showing results for “{searchQuery}” ({sortedPosts.length})
                                    {' '}
                                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={handleClearSearch}>
                                        Clear
                                    </button>
                                </p>
                            )}
                        </div>
                        <div className="col-md-auto">
                            <div className="sorting-filter-wrap">
                                <div className="nav" role="tablist">
                                    <button
                                        type="button"
                                        id="tab-destination-grid"
                                        className={`${activeTab === 'tab-grid' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('tab-grid')}
                                        aria-label="Grid view"
                                    >
                                        <i className="fa-light fa-grid-2" />
                                    </button>
                                    <button
                                        type="button"
                                        id="tab-destination-list"
                                        className={`${activeTab === 'tab-list' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('tab-list')}
                                        aria-label="List view"
                                    >
                                        <i className="fa-solid fa-list" />
                                    </button>
                                </div>
                                <form className="woocommerce-ordering" onSubmit={(e) => e.preventDefault()}>
                                    <select
                                        name="orderby"
                                        className="orderby"
                                        aria-label="destination order"
                                        value={orderBy}
                                        onChange={(e) => {
                                            setOrderBy(e.target.value)
                                            setCurrentPage(1)
                                        }}
                                    >
                                        <option value="menu_order">Default Sorting</option>
                                        <option value="popularity">Sort by popularity</option>
                                        <option value="rating">Sort by average rating</option>
                                        <option value="date">Sort by latest</option>
                                        <option value="price">Sort by price: low to high</option>
                                        <option value="price-desc">Sort by price: high to low</option>
                                    </select>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xxl-9 col-lg-8">
                        {!loading && !error && currentPosts.length === 0 && (
                            <p className="text-center py-5">
                                {searchQuery
                                    ? `No destinations found for “${searchQuery}”.`
                                    : 'No destinations published yet.'}
                            </p>
                        )}
                        <div className="tab-content" id="nav-tabContent">
                            <div className={`tab-pane fade ${activeTab === 'tab-grid' ? 'show active' : ''}`} id="tab-grid" role="tabpanel">
                                <div className="row gy-30">
                                    {currentPosts.map((data) => (
                                        <div key={data.id} className="col-xxl-4 col-xl-6">
                                            <DestinationCard
                                                destinationID={data.id}
                                                destinationImage={`${data.image}`}
                                                destinationTitle={data.title}
                                                destinationPrice={data.price}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={`tab-pane fade ${activeTab === 'tab-list' ? 'show active' : ''}`} id="tab-list" role="tabpanel">
                                <div className="row gy-30">
                                    {currentPosts.map((data) => (
                                        <div key={data.id} className="col-12">
                                            <DestinationCardTwo
                                                destinationID={data.id}
                                                destinationImage={`${data.image}`}
                                                destinationTitle={data.title}
                                                destinationPrice={data.price}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {totalPages > 1 && (
                            <div className="th-pagination text-center mt-60 mb-0">
                                <ul>
                                    {Array.from({ length: totalPages }, (_, i) => (
                                        <li key={i}>
                                            <Link
                                                className={currentPage === i + 1 ? 'active' : ''}
                                                to="#"
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    handlePageChange(i + 1)
                                                }}
                                            >
                                                {i + 1}
                                            </Link>
                                        </li>
                                    ))}
                                    {currentPage < totalPages && (
                                        <li>
                                            <Link
                                                className="next-page"
                                                to="#"
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    handlePageChange(currentPage + 1)
                                                }}
                                            >
                                                Next <img src="/assets/img/icon/arrow-right4.svg" alt="" />
                                            </Link>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                    <div className="col-xxl-3 col-lg-4">
                        <SidebarWidgets />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default DestinationInner
